import React, { useCallback, useMemo, useState } from "react";
import * as styles from "./velavu-alert-modal-text-confirm.module.scss";
import VelavuAlertModal from "./velavu-alert-modal";
import VelavuAlertModalTitle from "./velavu-alert-modal-title";
import VelavuAlertModalContent from "./velavu-alert-modal-content";
import VelavuAlertModalActions from "./velavu-alert-modal-actions";
import VelavuButton from "../velavu-button";
import VelavuInput from "../velavu-input";

export interface VelavuAlertModalTextConfirmProps {
	title: string;
	confirmText: string;
	confirmLabel?: string;
	cancelLabel?: string;
	isDanger?: boolean;
	children?: React.ReactNode;
	onResolve: (result: boolean) => void;
}

/**
 * A confirmation modal that requires the user to type a specific text to confirm.
 * Useful for dangerous operations like deletions.
 *
 * @example
 * pushModal<boolean>((resolve) => (
 *   <VelavuAlertModalTextConfirm
 *     title="Permanently delete 5 assets?"
 *     confirmText="permanently delete"
 *     confirmLabel="Delete"
 *     isDanger
 *     onResolve={resolve}
 *   >
 *     To confirm deletion, please type <i>permanently delete</i> in the field below
 *   </VelavuAlertModalTextConfirm>
 * ))
 */
export default function VelavuAlertModalTextConfirm(
	props: VelavuAlertModalTextConfirmProps,
) {
	const {
		title,
		confirmText,
		confirmLabel = "Confirm",
		cancelLabel = "Cancel",
		isDanger = false,
		children,
		onResolve,
	} = props;

	const [inputValue, setInputValue] = useState<string>("");

	const onChange = useCallback(
		(value: string) => {
			setInputValue(value);
		},
		[setInputValue],
	);

	const disabled = useMemo(() => {
		return inputValue.toLowerCase() !== confirmText.toLowerCase();
	}, [confirmText, inputValue]);

	return (
		<VelavuAlertModal>
			<VelavuAlertModalTitle>{title}</VelavuAlertModalTitle>

			<VelavuAlertModalContent>
				{children}

				<VelavuInput
					className={styles.textInput}
					onChange={onChange}
					placeholder={confirmText}
					italicPlaceholder
					fullWidth
				/>
			</VelavuAlertModalContent>

			<VelavuAlertModalActions>
				<VelavuButton
					label={cancelLabel}
					variant="outlined"
					fullWidth
					onClick={() => onResolve(false)}
				/>

				<VelavuButton
					label={confirmLabel}
					variant={isDanger ? "danger" : "normal"}
					disabled={disabled}
					fullWidth
					onClick={() => onResolve(true)}
				/>
			</VelavuAlertModalActions>
		</VelavuAlertModal>
	);
}
